/**
 * Menu configuration is derived from feature metadata.
 * This keeps sidebar generation data-driven and scalable.
 */

import {
  AcademicCapIcon,
  BuildingOffice2Icon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentCheckIcon,
  CurrencyRupeeIcon,
  HomeIcon,
  KeyIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  UsersIcon,
  Squares2X2Icon,
} from "@heroicons/react/24/outline";
import type { ComponentType } from "react";

import {
  canAny,
  getFeatureNavigationPermission,
  type FeatureMetadata,
  type NavigationPersona,
} from "@/features/feature.types";
import { FEATURES_BY_MODULE } from "@/features/featureRegistry";

type IconComponent = ComponentType<{ className?: string }>;

// Centralized module icon mapping (do not hardcode icons inside JSX components).
// Keys should match FEATURES_BY_MODULE module keys.
export const MODULE_SIDEBAR_ICONS: Record<string, IconComponent> = {
  home: HomeIcon,
  ai: SparklesIcon,
  academics: AcademicCapIcon,
  administration: BuildingOffice2Icon,
  communication: ChatBubbleLeftRightIcon,
  examinations: ClipboardDocumentCheckIcon,
  feesFinance: CurrencyRupeeIcon,
  humanResources: UsersIcon,
  infrastructure: WrenchScrewdriverIcon,
  userAccess: KeyIcon,
};

export const DEFAULT_FEATURE_ICON: IconComponent = Squares2X2Icon;

type ModuleIconTone = {
  active: string;
  inactive: string;
};

// Theme-aware, colorful icon accents per module.
// Uses Tailwind's existing palette (no new icon set, no inline hex colors).
export const MODULE_SIDEBAR_ICON_TONES: Record<string, ModuleIconTone> = {
  home: {
    active: "text-blue-600 dark:text-blue-400",
    inactive: "text-blue-600 dark:text-blue-400 opacity-80",
  },
  ai: {
    active: "text-violet-600 dark:text-violet-400",
    inactive: "text-violet-600 dark:text-violet-400 opacity-80",
  },
  academics: {
    active: "text-indigo-600 dark:text-indigo-400",
    inactive: "text-indigo-600 dark:text-indigo-400 opacity-80",
  },
  administration: {
    active: "text-amber-600 dark:text-amber-400",
    inactive: "text-amber-600 dark:text-amber-400 opacity-80",
  },
  communication: {
    active: "text-cyan-600 dark:text-cyan-400",
    inactive: "text-cyan-600 dark:text-cyan-400 opacity-80",
  },
  examinations: {
    active: "text-rose-600 dark:text-rose-400",
    inactive: "text-rose-600 dark:text-rose-400 opacity-80",
  },
  feesFinance: {
    active: "text-emerald-600 dark:text-emerald-400",
    inactive: "text-emerald-600 dark:text-emerald-400 opacity-80",
  },
  humanResources: {
    active: "text-teal-600 dark:text-teal-400",
    inactive: "text-teal-600 dark:text-teal-400 opacity-80",
  },
  infrastructure: {
    active: "text-orange-600 dark:text-orange-400",
    inactive: "text-orange-600 dark:text-orange-400 opacity-80",
  },
  userAccess: {
    active: "text-red-600 dark:text-red-400",
    inactive: "text-red-600 dark:text-red-400 opacity-80",
  },
};

export function getModuleSidebarIconTone(moduleKey: string, active: boolean): string {
  const tone = MODULE_SIDEBAR_ICON_TONES[moduleKey] ?? {
    active: "text-sky-600 dark:text-sky-400",
    inactive: "text-sky-600 dark:text-sky-400 opacity-80",
  };

  return active ? tone.active : tone.inactive;
}

function getModuleSidebarIcon(moduleKey: string): IconComponent {
  return MODULE_SIDEBAR_ICONS[moduleKey] ?? DEFAULT_FEATURE_ICON;
}

// Group labels the title helper would get wrong ("ai" would read "Ai").
export const MODULE_SIDEBAR_LABELS: Record<string, string> = {
  ai: "AI",
};

export interface MenuItem {
  id: string;
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  toneKey?: string;
  description?: string;
  permission?: string | string[]; // e.g., "Module.Feature.view", or several where any one suffices
  badge?: string;
  badge_color?: "red" | "blue" | "green" | "yellow" | "purple" | "gray";
  children?: MenuItem[];
}

export interface MenuGroup {
  id: string;
  name: string;
  icon?: ComponentType<{ className?: string }>;
  description?: string;
  items: MenuItem[];
  collapsible?: boolean;
  defaultOpen?: boolean;
}

export interface MenuSection {
  id: string;
  title: string;
  groups: MenuGroup[];
}
// ============================================================================
// DATA-DRIVEN BUILDERS
// ============================================================================

function titleizeModuleKey(moduleKey: string): string {
  const normalized = String(moduleKey ?? "").trim();
  if (!normalized) return "Module";

  // Insert spaces into camelCase / PascalCase.
  const spaced = normalized
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]+/g, " ")
    .trim();

  return spaced
    .split(/\s+/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(" ");
}

function featureToMenuItem(feature: FeatureMetadata): MenuItem {
  return {
    id: feature.id,
    name: feature.navigation.label,
    href: feature.navigation.path,
    icon: feature.navigation.icon ?? DEFAULT_FEATURE_ICON,
    toneKey: feature.moduleName,
    permission: getFeatureNavigationPermission(feature, "staff"),
  };
}

function buildStaffMenuItems(
  features: readonly FeatureMetadata[],
  hasPermission?: (permission?: string) => boolean,
): MenuItem[] {
  const orderedFeatures = features.slice().sort((a, b) => {
    const orderDiff = (a.navigation.order ?? 0) - (b.navigation.order ?? 0);
    return orderDiff !== 0
      ? orderDiff
      : a.navigation.label.localeCompare(b.navigation.label);
  });

  const rootItems: MenuItem[] = [];
  const rootItemsById = new Map<string, MenuItem>();
  const childrenByParent = new Map<string, MenuItem[]>();

  for (const feature of orderedFeatures) {
    const item = featureToMenuItem(feature);

    if (hasPermission && !canAny(hasPermission, item.permission)) {
      continue;
    }

    const parentId = feature.navigation.parentId;

    if (!parentId) {
      rootItems.push(item);
      rootItemsById.set(feature.id, item);
      continue;
    }

    const siblings = childrenByParent.get(parentId) ?? [];
    siblings.push(item);
    childrenByParent.set(parentId, siblings);
  }

  const orphanChildren: MenuItem[] = [];

  for (const [parentId, children] of childrenByParent.entries()) {
    const parent = rootItemsById.get(parentId);

    if (parent) {
      parent.children = children;
      continue;
    }

    orphanChildren.push(...children);
  }

  return [...rootItems, ...orphanChildren];
}

function flattenMenuItems(items: readonly MenuItem[]): MenuItem[] {
  return items.flatMap((item) =>
    item.children && item.children.length > 0
      ? [item, ...flattenMenuItems(item.children)]
      : [item],
  );
}

function filterMenuItemsByPermission(
  items: readonly MenuItem[],
  hasPermission: (permission?: string) => boolean,
): MenuItem[] {
  return items
    .map((item) => {
      const children = item.children
        ? filterMenuItemsByPermission(item.children, hasPermission)
        : undefined;

      return {
        ...item,
        children,
      } satisfies MenuItem;
    })
    .filter(
      (item) =>
        canAny(hasPermission, item.permission) || (item.children?.length ?? 0) > 0,
    );
}

function featureToGuardianMenuItem(feature: FeatureMetadata): MenuItem {
  return {
    id: feature.id,
    name: feature.guardianNavigation?.label ?? feature.navigation.label,
    href: feature.navigation.path,
    icon: feature.navigation.icon ?? DEFAULT_FEATURE_ICON,
    toneKey: feature.moduleName,
    permission: getFeatureNavigationPermission(feature, "guardian"),
  };
}

export function isFeatureVisibleInNavigation(
  feature: FeatureMetadata,
  persona: NavigationPersona,
): boolean {
  return persona === "guardian" ? Boolean(feature.guardianNavigation) : true;
}

export function buildMenuSectionsFromFeatures(
  featuresByModule: Record<string, readonly FeatureMetadata[]>,
  hasPermission?: (permission?: string) => boolean,
  options?: { persona?: NavigationPersona },
): MenuSection[] {
  const persona = options?.persona ?? "staff";

  if (persona === "guardian") {
    const items = Object.values(featuresByModule)
      .flatMap((features) => features)
      .filter((feature) => isFeatureVisibleInNavigation(feature, persona))
      .slice()
      .sort((a, b) => {
        const orderDiff =
          (a.guardianNavigation?.order ?? a.navigation.order ?? 0) -
          (b.guardianNavigation?.order ?? b.navigation.order ?? 0);
        return orderDiff !== 0
          ? orderDiff
          : (a.guardianNavigation?.label ?? a.navigation.label).localeCompare(
              b.guardianNavigation?.label ?? b.navigation.label,
            );
      })
      .map(featureToGuardianMenuItem)
      .filter((item) => (hasPermission ? canAny(hasPermission, item.permission) : true));

    return items.length
      ? ([
          {
            id: "guardian",
            title: "My Space",
            groups: [
              {
                id: "guardian",
                name: "Quick Access",
                items,
                collapsible: false,
              },
            ],
          },
        ] satisfies MenuSection[])
      : [];
  }

  const moduleEntries = Object.entries(featuresByModule).filter(
    ([, features]) => Array.isArray(features) && features.length > 0,
  );

  const groups: MenuGroup[] = moduleEntries
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([moduleKey, features]) => {
      const items = buildStaffMenuItems(features, hasPermission);

      return {
        id: moduleKey,
        name: MODULE_SIDEBAR_LABELS[moduleKey] ?? titleizeModuleKey(moduleKey),
        icon: getModuleSidebarIcon(moduleKey),
        items,
        collapsible: true,
        defaultOpen: moduleKey === "academics",
      } satisfies MenuGroup;
    })
    .filter((group) => group.items.length > 0);

  return groups.length
    ? ([
        {
          id: "modules",
          title: "Modules",
          groups,
        },
      ] satisfies MenuSection[])
    : [];
}

export const MAIN_MENU_SECTIONS: MenuSection[] =
  buildMenuSectionsFromFeatures(FEATURES_BY_MODULE);

export const ALL_MENU_ITEMS: MenuItem[] = MAIN_MENU_SECTIONS.flatMap((section) =>
  section.groups.flatMap((group) => flattenMenuItems(group.items)),
);

export function getMenuItemById(id: string): MenuItem | undefined {
  return ALL_MENU_ITEMS.find((item) => item.id === id);
}

export function getMenuItemByHref(href: string): MenuItem | undefined {
  return ALL_MENU_ITEMS.find((item) => item.href === href);
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export function filterMenuByPermission(
  menu: MenuSection[],
  hasPermission: (permission?: string) => boolean,
): MenuSection[] {
  return menu
    .map((section) => ({
      ...section,
      groups: section.groups
        .map((group) => ({
          ...group,
          items: filterMenuItemsByPermission(group.items, hasPermission),
        }))
        .filter((group) => group.items.length > 0),
    }))
    .filter((section) => section.groups.length > 0);
}

export function getPermissionPath(
  category: string,
  subcategory: string,
  action: string,
): string {
  return `${category}.${subcategory}.${action}`;
}
